import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MediaInput } from "@/components/bi/MediaInput";
import { KPI_ICONS } from "@/components/bi/KpiCard";
import { FONT_FAMILIES } from "@/components/bi/FontStyleControls";
import { Gauge } from "lucide-react";

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <Label className="text-[11px] text-[color:var(--ink-3)] shrink-0">{label}</Label>
      {children}
    </div>
  );
}
function Color({ value, fallback, onChange, testid }) {
  return (
    <div className="flex items-center gap-1">
      <input data-testid={testid} type="color" value={value || fallback} onChange={(e) => onChange(e.target.value)} className="h-7 w-9 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
      {value && <button onClick={() => onChange("")} className="text-[10px] text-[color:var(--ink-3)] hover:text-[color:var(--ink)]">reset</button>}
    </div>
  );
}

// KPI configuration (items 66-73): layouts, icon, target vs actual, benchmark, indicators, sparkline, formatting
export function KpiEditor({ config, opts, patchOpts, fields = [] }) {
  const k = opts.kpi || {};
  const set = (patch) => patchOpts({ kpi: { ...k, ...patch } });
  const setIcon = (patch) => set({ icon: { ...(k.icon || {}), ...patch } });
  const setTarget = (patch) => set({ target: { ...(k.target || {}), ...patch } });
  const setBench = (patch) => set({ benchmark: { ...(k.benchmark || {}), ...patch } });
  const setInd = (patch) => set({ indicator: { ...(k.indicator || {}), ...patch } });
  const setSpark = (patch) => set({ sparkline: { ...(k.sparkline || {}), ...patch } });
  const hasSecondMeasure = (config.measures || []).length > 1;

  return (
    <div data-testid="kpi-editor" className="space-y-2">
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <Label className="text-[11px] text-[color:var(--ink-3)]">Layout</Label>
          <Select value={k.layout || "classic"} onValueChange={(v) => set({ layout: v })}>
            <SelectTrigger data-testid="kpi-layout" className="mt-1 bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="classic">Classic</SelectItem>
              <SelectItem value="compact">Compact</SelectItem>
              <SelectItem value="hero">Hero (centered)</SelectItem>
              <SelectItem value="split">Split (spark right)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] text-[color:var(--ink-3)]">Value size</Label>
          <Select value={k.valueSize || "md"} onValueChange={(v) => set({ valueSize: v })}>
            <SelectTrigger data-testid="kpi-value-size" className="mt-1 bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="md">Medium</SelectItem>
              <SelectItem value="lg">Large</SelectItem>
              <SelectItem value="xl">Extra large</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Row label="Center align"><Switch data-testid="kpi-align-center" checked={k.align === "center"} onCheckedChange={(v) => set({ align: v ? "center" : "left" })} /></Row>

      {/* Item 5: internal card grid — 1×4, 2×2 … inside this single widget */}
      <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
        <Label className="text-[11px] font-semibold text-[color:var(--ink-2)]">Card grid</Label>
        <div className="grid grid-cols-2 gap-1.5">
          <div>
            <Label className="text-[10px] text-[color:var(--ink-3)]">Row count</Label>
            <Input data-testid="kpi-grid-rows" type="number" min={1} max={6} value={k.grid?.rows ?? 1}
              onChange={(e) => set({ grid: { ...(k.grid || {}), rows: Math.max(1, Math.min(6, Number(e.target.value) || 1)) } })}
              className="mt-0.5 bg-[color:var(--surface-2)] h-7 text-xs" />
          </div>
          <div>
            <Label className="text-[10px] text-[color:var(--ink-3)]">Column count</Label>
            <Input data-testid="kpi-grid-cols" type="number" min={1} max={6} value={k.grid?.cols ?? 1}
              onChange={(e) => set({ grid: { ...(k.grid || {}), cols: Math.max(1, Math.min(6, Number(e.target.value) || 1)) } })}
              className="mt-0.5 bg-[color:var(--surface-2)] h-7 text-xs" />
          </div>
        </div>
        <p className="text-[10px] text-[color:var(--ink-3)] leading-tight">
          {(Number(k.grid?.rows) || 1) * (Number(k.grid?.cols) || 1) > 1
            ? "One card per category when a dimension is assigned (e.g. Region), otherwise one card per measure. Extra categories scroll."
            : "Set a row × column count (e.g. 1 × 4 or 2 × 2) to show several cards inside this one widget."}
        </p>
      </div>
      <Row label="Value color"><Color testid="kpi-value-color" value={k.valueColor} fallback="#1A2440" onChange={(v) => set({ valueColor: v })} /></Row>
      <Row label="Label color"><Color testid="kpi-label-color" value={k.labelColor} fallback="#8B96AD" onChange={(v) => set({ labelColor: v })} /></Row>

      <SectionsEditor k={k} set={set} />

      <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
        <Row label="Show icon"><Switch data-testid="kpi-icon-show" checked={!!k.icon?.show} onCheckedChange={(v) => setIcon({ show: v })} /></Row>
        {k.icon?.show && (
          <>
            <div className="flex gap-1.5">
              <Select value={k.icon?.name || "target"} onValueChange={(v) => setIcon({ name: v, image: "" })}>
                <SelectTrigger data-testid="kpi-icon-name" className="bg-[color:var(--surface-2)] flex-1 h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(KPI_ICONS).map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
              </Select>
              <Color testid="kpi-icon-color" value={k.icon?.color} fallback="#2B4EFF" onChange={(v) => setIcon({ color: v })} />
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              <div>
                <Label className="text-[10px] text-[color:var(--ink-3)]">Position</Label>
                <Select value={k.icon?.position || "left"} onValueChange={(v) => setIcon({ position: v })}>
                  <SelectTrigger data-testid="kpi-icon-position" className="mt-0.5 bg-[color:var(--surface-2)] h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">Left of value</SelectItem>
                    <SelectItem value="right">Right of value</SelectItem>
                    <SelectItem value="top">Above value</SelectItem>
                    <SelectItem value="top-right">Top-right corner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-[color:var(--ink-3)]">Size (px)</Label>
                <Input data-testid="kpi-icon-size" type="number" min={14} max={96} value={k.icon?.size ?? 32}
                  onChange={(e) => setIcon({ size: Math.max(14, Math.min(96, Number(e.target.value) || 32)) })}
                  className="mt-0.5 bg-[color:var(--surface-2)] h-7 text-xs" />
              </div>
            </div>
            {k.icon?.image && (
              <div>
                <Label className="text-[10px] text-[color:var(--ink-3)]">Image fit</Label>
                <Select value={k.icon?.fit || "cover"} onValueChange={(v) => setIcon({ fit: v })}>
                  <SelectTrigger data-testid="kpi-icon-fit" className="mt-0.5 bg-[color:var(--surface-2)] h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cover">Cover (fill the box)</SelectItem>
                    <SelectItem value="contain">Contain (uniform)</SelectItem>
                    <SelectItem value="fill">Stretch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-[10px] text-[color:var(--ink-3)]">…or a custom image</Label>
              <div className="mt-1"><MediaInput testid="kpi-icon-image" value={k.icon?.image || ""} onChange={(v) => setIcon({ image: v })} /></div>
            </div>
          </>
        )}
      </div>

      <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
        <Label className="text-[11px] text-[color:var(--ink-2)] font-medium">Target vs actual</Label>
        <Select value={k.target?.mode || "none"} onValueChange={(v) => setTarget({ mode: v })}>
          <SelectTrigger data-testid="kpi-target-mode" className="bg-[color:var(--surface-2)] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No target</SelectItem>
            <SelectItem value="static">Fixed target value</SelectItem>
            <SelectItem value="measure">Second measure as target</SelectItem>
            <SelectItem value="query">Independent field (own source)</SelectItem>
          </SelectContent>
        </Select>
        {k.target?.mode === "static" && (
          <Input data-testid="kpi-target-value" type="number" value={k.target?.value ?? ""} onChange={(e) => setTarget({ value: e.target.value })} placeholder="e.g. 100000" className="bg-[color:var(--surface-2)] h-7 text-xs" />
        )}
        {k.target?.mode === "measure" && !hasSecondMeasure && (
          <p className="text-[10px] text-[color:#F59E0B]">Add a second measure in the Data section — it becomes the target.</p>
        )}
        {k.target?.mode === "query" && (
          <>
            <div className="grid grid-cols-2 gap-1.5">
              <Select value={k.target?.field || ""} onValueChange={(v) => setTarget({ field: v })}>
                <SelectTrigger data-testid="kpi-target-field" className="bg-[color:var(--surface-2)] h-7 text-xs"><SelectValue placeholder="Target field" /></SelectTrigger>
                <SelectContent>{fields.map((f) => <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={k.target?.agg || "sum"} onValueChange={(v) => setTarget({ agg: v })}>
                <SelectTrigger data-testid="kpi-target-agg" className="bg-[color:var(--surface-2)] h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{["sum", "avg", "min", "max", "median", "count", "count_distinct"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Row label="Respect dashboard filters"><Switch data-testid="kpi-target-respect-filters" checked={k.target?.respectFilters !== false} onCheckedChange={(v) => setTarget({ respectFilters: v })} /></Row>
            <p className="text-[10px] text-[color:var(--ink-3)]">The target is queried independently of the actual — its own field, aggregation and filter behavior.</p>
          </>
        )}
        {(k.target?.mode || "none") !== "none" && (
          <>
            <div className="grid grid-cols-2 gap-1.5">
              <div>
                <Label className="text-[10px] text-[color:var(--ink-3)]">Show comparison as</Label>
                <Select value={k.target?.compareAs || "both"} onValueChange={(v) => setTarget({ compareAs: v })}>
                  <SelectTrigger data-testid="kpi-target-compare-as" className="mt-0.5 bg-[color:var(--surface-2)] h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (hide comparison)</SelectItem>
                    <SelectItem value="both">% + variance</SelectItem>
                    <SelectItem value="percent">Achievement % only</SelectItem>
                    <SelectItem value="absolute">Variance only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-[color:var(--ink-3)]">Progress style</Label>
                <Select value={k.target?.display || "bar"} onValueChange={(v) => setTarget({ display: v })}>
                  <SelectTrigger data-testid="kpi-target-display" className="mt-0.5 bg-[color:var(--surface-2)] h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bar">Progress bar</SelectItem>
                    <SelectItem value="bullet">Bullet (target tick)</SelectItem>
                    <SelectItem value="none">Text only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Input data-testid="kpi-target-label" value={k.target?.label || ""} onChange={(e) => setTarget({ label: e.target.value })} placeholder="Target label (default: Target)" className="bg-[color:var(--surface-2)] h-7 text-xs" />
            <div className="grid grid-cols-2 gap-1.5">
              <Row label="Value when met"><Color testid="kpi-target-above-color" value={k.target?.aboveColor} fallback="#16A34A" onChange={(v) => setTarget({ aboveColor: v })} /></Row>
              <Row label="Value when missed"><Color testid="kpi-target-below-color" value={k.target?.belowColor} fallback="#DC2626" onChange={(v) => setTarget({ belowColor: v })} /></Row>
            </div>
            <p className="text-[10px] text-[color:var(--ink-3)]">Shows target, achievement % and variance. Conditional formatting rules still take priority over target colors.</p>
          </>
        )}
      </div>

      <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
        <Label className="text-[11px] text-[color:var(--ink-2)] font-medium">Comparison benchmark</Label>
        <Select value={k.benchmark?.mode || "none"} onValueChange={(v) => setBench({ mode: v })}>
          <SelectTrigger data-testid="kpi-bench-mode" className="bg-[color:var(--surface-2)] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            <SelectItem value="static">Fixed benchmark value</SelectItem>
            <SelectItem value="measure">Second measure</SelectItem>
          </SelectContent>
        </Select>
        {k.benchmark?.mode === "static" && (
          <Input data-testid="kpi-bench-value" type="number" value={k.benchmark?.value ?? ""} onChange={(e) => setBench({ value: e.target.value })} placeholder="e.g. last year: 90000" className="bg-[color:var(--surface-2)] h-7 text-xs" />
        )}
        {(k.benchmark?.mode || "none") !== "none" && (
          <Input data-testid="kpi-bench-label" value={k.benchmark?.label || ""} onChange={(e) => setBench({ label: e.target.value })} placeholder="Label, e.g. vs last year" className="bg-[color:var(--surface-2)] h-7 text-xs" />
        )}
        <p className="text-[10px] text-[color:var(--ink-3)]">For previous-period comparison use “Compare over time” in the Data section.</p>
      </div>

      <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
        <Label className="text-[11px] text-[color:var(--ink-2)] font-medium flex items-center gap-1"><Gauge className="w-3 h-3" />Status indicator</Label>
        <div className="grid grid-cols-2 gap-1.5">
          <Select value={k.indicator?.style || "arrow"} onValueChange={(v) => setInd({ style: v })}>
            <SelectTrigger data-testid="kpi-ind-style" className="bg-[color:var(--surface-2)] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="arrow">Up/down arrow</SelectItem>
              <SelectItem value="traffic">Traffic light</SelectItem>
              <SelectItem value="badge">Status badge</SelectItem>
              <SelectItem value="none">None</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center justify-between gap-1 px-1">
            <Label className="text-[10px] text-[color:var(--ink-3)]">Higher is better</Label>
            <Switch data-testid="kpi-higher-better" checked={k.indicator?.higherIsBetter !== false} onCheckedChange={(v) => setInd({ higherIsBetter: v })} />
          </div>
        </div>
        {(k.target?.mode || "none") !== "none" && (
          <div className="grid grid-cols-2 gap-1.5">
            <div><Label className="text-[10px] text-[color:var(--ink-3)]">Good at ≥ %</Label><Input data-testid="kpi-good-at" type="number" value={k.indicator?.goodAt ?? 100} onChange={(e) => setInd({ goodAt: Number(e.target.value) || 100 })} className="mt-0.5 bg-[color:var(--surface-2)] h-7 text-xs" /></div>
            <div><Label className="text-[10px] text-[color:var(--ink-3)]">Warn at ≥ %</Label><Input data-testid="kpi-warn-at" type="number" value={k.indicator?.warnAt ?? 80} onChange={(e) => setInd({ warnAt: Number(e.target.value) || 80 })} className="mt-0.5 bg-[color:var(--surface-2)] h-7 text-xs" /></div>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
        <Row label="Sparkline"><Switch data-testid="kpi-spark-show" checked={k.sparkline?.show !== false} onCheckedChange={(v) => setSpark({ show: v })} /></Row>
        {k.sparkline?.show !== false && (
          <div className="flex gap-1.5 items-center">
            <Select value={k.sparkline?.style || "area"} onValueChange={(v) => setSpark({ style: v })}>
              <SelectTrigger data-testid="kpi-spark-style" className="bg-[color:var(--surface-2)] flex-1 h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="area">Area</SelectItem>
                <SelectItem value="line">Line</SelectItem>
                <SelectItem value="bar">Bars</SelectItem>
              </SelectContent>
            </Select>
            <Input data-testid="kpi-spark-points" type="number" min={2} max={365} value={k.sparkline?.points ?? 60} onChange={(e) => setSpark({ points: Number(e.target.value) || 60 })} title="Max points" className="bg-[color:var(--surface-2)] w-16 h-7 text-xs" />
            <Color testid="kpi-spark-color" value={k.sparkline?.color} fallback="#2B4EFF" onChange={(v) => setSpark({ color: v })} />
          </div>
        )}
        <p className="text-[10px] text-[color:var(--ink-3)]">Sparkline needs a date/trend dimension (set via “Compare over time” or a dimension).</p>
      </div>
    </div>
  );
}

// Items 22/23: per-section position + independent formatting (font size / weight / color).
const SECTION_DEFS = [
  { key: "value", label: "Actual value", positions: null },
  { key: "label", label: "Label", positions: [["below", "Below value"], ["above", "Above value"], ["hidden", "Hidden"]] },
  { key: "target", label: "Target", positions: [["below", "Below value"], ["right", "Right column"], ["hidden", "Hidden"]], extra: "target" },
  { key: "comparison", label: "Comparison (Δ / %)", positions: [["inline", "Inline with value"], ["below", "Below value"], ["right", "Right column"], ["hidden", "Hidden"]], extra: "comparison" },
  { key: "indicator", label: "Status indicator", positions: [["inline", "Inline with value"], ["left", "Left of value"], ["right", "Right column"], ["top", "Top row"], ["hidden", "Hidden"]] },
  { key: "spark", label: "Sparkline", positions: [["bottom", "Bottom"], ["right", "Right"], ["background", "Background"], ["hidden", "Hidden"]], extra: "spark" },
];
const WEIGHTS = [["", "Default"], ["400", "Regular"], ["500", "Medium"], ["600", "Semibold"], ["700", "Bold"]];

function SectionsEditor({ k, set }) {
  const S = k.sections || {};
  const setSec = (key, patch) => set({ sections: { ...S, [key]: { ...(S[key] || {}), ...patch } } });
  return (
    <div data-testid="kpi-sections-editor" className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
      <Label className="text-[11px] text-[color:var(--ink-2)] font-medium">Sections — position &amp; formatting</Label>
      <p className="text-[10px] text-[color:var(--ink-3)]">Each part of the card can be placed and styled independently; sections never overlap.</p>
      {SECTION_DEFS.map((d) => {
        const s = S[d.key] || {};
        return (
          <div key={d.key} data-testid={`kpi-section-${d.key}`} className="rounded-md bg-[color:var(--surface-2)] px-2 py-1.5 space-y-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-[color:var(--ink-2)] font-medium">{d.label}</span>
              {d.positions && (
                <Select value={s.position || d.positions[0][0]} onValueChange={(v) => setSec(d.key, { position: v })}>
                  <SelectTrigger data-testid={`kpi-section-${d.key}-position`} className="h-6 w-[136px] text-[10px] bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                  <SelectContent>{d.positions.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <Input data-testid={`kpi-section-${d.key}-size`} type="number" min={8} max={120} placeholder="px" value={s.fontSize ?? ""}
                onChange={(e) => setSec(d.key, { fontSize: e.target.value === "" ? undefined : Number(e.target.value) })}
                title="Font size (px)" className="h-6 w-16 text-[10px] bg-[color:var(--surface)]" />
              <Select value={s.weight || "__def"} onValueChange={(v) => setSec(d.key, { weight: v === "__def" ? undefined : v })}>
                <SelectTrigger data-testid={`kpi-section-${d.key}-weight`} className="h-6 flex-1 text-[10px] bg-[color:var(--surface)]"><SelectValue placeholder="Weight" /></SelectTrigger>
                <SelectContent>{WEIGHTS.map(([v, l]) => <SelectItem key={v || "def"} value={v || "__def"}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <Color testid={`kpi-section-${d.key}-color`} value={s.color} fallback="#1A2440" onChange={(v) => setSec(d.key, { color: v || undefined })} />
            </div>
            {/* Item 22: independent font family + horizontal / vertical alignment per section */}
            <div className="flex items-center gap-1.5">
              <Select value={s.family || "__def"} onValueChange={(v) => setSec(d.key, { family: v === "__def" ? undefined : v })}>
                <SelectTrigger data-testid={`kpi-section-${d.key}-family`} className="h-6 flex-1 min-w-0 text-[10px] bg-[color:var(--surface)]"><SelectValue placeholder="Font" /></SelectTrigger>
                <SelectContent>{FONT_FAMILIES.map((f) => <SelectItem key={f.v || "def"} value={f.v || "__def"}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={s.align || "__def"} onValueChange={(v) => setSec(d.key, { align: v === "__def" ? undefined : v })}>
                <SelectTrigger data-testid={`kpi-section-${d.key}-align`} className="h-6 w-[74px] text-[10px] bg-[color:var(--surface)]"><SelectValue placeholder="Align" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__def">Auto</SelectItem>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
              {d.key === "value" && (
                <Select value={s.valign || "__def"} onValueChange={(v) => setSec(d.key, { valign: v === "__def" ? undefined : v })}>
                  <SelectTrigger data-testid={`kpi-section-${d.key}-valign`} className="h-6 w-[74px] text-[10px] bg-[color:var(--surface)]"><SelectValue placeholder="V-align" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__def">Middle</SelectItem>
                    <SelectItem value="top">Top</SelectItem>
                    <SelectItem value="center">Center</SelectItem>
                    <SelectItem value="bottom">Bottom</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            {d.extra === "target" && (
              <div className="flex items-center gap-1.5">
                <Input data-testid="kpi-section-target-valuesize" type="number" min={8} max={120} placeholder="value px" value={s.valueFontSize ?? ""}
                  onChange={(e) => setSec(d.key, { valueFontSize: e.target.value === "" ? undefined : Number(e.target.value) })}
                  title="Target value font size (px)" className="h-6 w-[70px] text-[10px] bg-[color:var(--surface)]" />
                <Select value={s.valueWeight || "__def"} onValueChange={(v) => setSec(d.key, { valueWeight: v === "__def" ? undefined : v })}>
                  <SelectTrigger data-testid="kpi-section-target-valueweight" className="h-6 flex-1 text-[10px] bg-[color:var(--surface)]"><SelectValue placeholder="Value weight" /></SelectTrigger>
                  <SelectContent>{WEIGHTS.map(([v, l]) => <SelectItem key={v || "def"} value={v || "__def"}>{l}</SelectItem>)}</SelectContent>
                </Select>
                <Color testid="kpi-section-target-valuecolor" value={s.valueColor} fallback="#1A2440" onChange={(v) => setSec(d.key, { valueColor: v || undefined })} />
                <Color testid="kpi-section-target-labelcolor" value={s.labelColor} fallback="#8B96AD" onChange={(v) => setSec(d.key, { labelColor: v || undefined })} />
                <Select value={s.valueFamily || "__def"} onValueChange={(v) => setSec(d.key, { valueFamily: v === "__def" ? undefined : v })}>
                  <SelectTrigger data-testid="kpi-section-target-valuefamily" className="h-6 w-[92px] text-[10px] bg-[color:var(--surface)]"><SelectValue placeholder="Value font" /></SelectTrigger>
                  <SelectContent>{FONT_FAMILIES.map((f) => <SelectItem key={f.v || "__def"} value={f.v || "__def"}>{f.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {d.extra === "target" && (
              <div className="flex items-center gap-1.5">
                <Select value={s.barStyle || k.target?.display || "bar"} onValueChange={(v) => setSec(d.key, { barStyle: v })}>
                  <SelectTrigger data-testid="kpi-section-target-bar" className="h-6 flex-1 text-[10px] bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="bar">Progress bar</SelectItem><SelectItem value="bullet">Bullet</SelectItem><SelectItem value="none">No bar</SelectItem></SelectContent>
                </Select>
                <Input data-testid="kpi-section-target-barh" type="number" min={2} max={24} placeholder="bar px" value={s.barHeight ?? ""} onChange={(e) => setSec(d.key, { barHeight: e.target.value === "" ? undefined : Number(e.target.value) })} title="Bar height (px)" className="h-6 w-16 text-[10px] bg-[color:var(--surface)]" />
                <Color testid="kpi-section-target-barcolor" value={s.barColor} fallback="#00C2A8" onChange={(v) => setSec(d.key, { barColor: v || undefined })} />
              </div>
            )}
            {d.extra === "comparison" && (
              <div className="flex items-center gap-3">
                <Row label="Good"><Color testid="kpi-section-cmp-good" value={s.goodColor} fallback="#00C2A8" onChange={(v) => setSec(d.key, { goodColor: v || undefined })} /></Row>
                <Row label="Bad"><Color testid="kpi-section-cmp-bad" value={s.badColor} fallback="#E85D6A" onChange={(v) => setSec(d.key, { badColor: v || undefined })} /></Row>
              </div>
            )}
            {d.extra === "spark" && (
              <Row label="Height (px)"><Input data-testid="kpi-section-spark-h" type="number" min={16} max={200} value={s.height ?? 40} onChange={(e) => setSec(d.key, { height: Number(e.target.value) || 40 })} className="h-6 w-16 text-[10px] bg-[color:var(--surface)]" /></Row>
            )}
          </div>
        );
      })}
    </div>
  );
}
